// =============================================================================
// Network-failure retry for the SPA → Pages Functions calls
// =============================================================================
// Deliberately free of React/Clerk imports so it can be exercised directly by a
// test harness. api.ts is the only caller.
//
// Why this exists (2026-08-03 incident): real users mid-signup saw a red
// "Load failed" — Safari's raw text for a fetch that never got a response —
// rendered verbatim to Hebrew speakers with no retry and no instruction. They
// gave up and landed in the stuck-user list. Same failure class as the earlier
// disabled-button bug: the form fails in a way you cannot act on.
// =============================================================================

/**
 * A `fetch` that never resolved — no response at all. Distinct from an HTTP
 * error, which is an ApiError. Carries a Hebrew, user-facing message because
 * the raw browser text is untranslated and unactionable: Safari throws
 * `TypeError: Load failed`, Chrome `Failed to fetch`.
 */
export class NetworkError extends Error {
  constructor() {
    super("החיבור לשרת נכשל. בדקו את החיבור לאינטרנט ונסו שוב — הפרטים נשמרו בטופס.")
    this.name = "NetworkError"
  }
}

/** Backoff between attempts, in ms. Length + 1 = total attempts. */
export const RETRY_BACKOFF_MS = [400, 1200]

/**
 * Fetch with retries on NETWORK failure only — never on an HTTP error status.
 *
 * Why retrying a POST is safe here: `/api/supporters` is idempotent. The app
 * checks `resolveSupporterId` first, and the database enforces it regardless
 * via a UNIQUE index on `supporters.clerk_user_id`. So if the request DID
 * reach the server and only the response was lost, the retry returns the same
 * supporter with `created: false` instead of creating a duplicate.
 *
 * `sleep` is injectable so tests do not wait on real backoff.
 */
export async function fetchWithRetry(
  path: string,
  init: RequestInit,
  deps: {
    fetchFn?: typeof fetch
    sleep?: (ms: number) => Promise<void>
  } = {},
): Promise<Response> {
  const fetchFn = deps.fetchFn ?? fetch
  const sleep = deps.sleep ?? ((ms: number) => new Promise<void>((r) => setTimeout(r, ms)))

  for (let attempt = 0; ; attempt++) {
    try {
      return await fetchFn(path, init)
    } catch {
      // fetch only rejects on a network-level failure; HTTP errors resolve.
      if (attempt >= RETRY_BACKOFF_MS.length) throw new NetworkError()
      await sleep(RETRY_BACKOFF_MS[attempt])
    }
  }
}
